// The company's own calendar for the entry forms (S1-17 review #7): a week's days and the
// default period of a cadence come from the company's `calendar_config` (week start, time
// zone), through the same domain-kpi functions the API stamps `period_key` with — never ISO
// Mondays or the browser's clock zone.
import {
  dayKeysInPeriod,
  type OrgCalendarConfig,
  periodFromKey,
  periodKeyFor,
} from "@companyos/domain-kpi";

export type CompanyCalendar = OrgCalendarConfig;

/** The days (YYYY-MM-DD) of week `week` in the company's calendar. */
export function companyWeekDays(cal: CompanyCalendar, week: string): string[] {
  try {
    return dayKeysInPeriod(cal, periodFromKey(cal, "week", week));
  } catch {
    return [];
  }
}

export type EntryPeriodKind = "day" | "week" | "month" | "quarter" | "year";

/** The company's current period of a kind at `now` (a quarter and a year are fiscal). */
export function companyPeriodNow(
  cal: CompanyCalendar,
  kind: EntryPeriodKind,
  now: Date = new Date(),
): string {
  return periodKeyFor(cal, kind, now);
}

/**
 * The company's current quarter or year and the `count - 1` before it, newest first (KPI-FIX
 * #2): the choices of a quarterly or yearly metric, which has no native date input. Never a
 * future period, which the API refuses.
 */
export function recentPeriods(
  cal: CompanyCalendar,
  kind: "quarter" | "year",
  count: number,
  now: Date = new Date(),
): { key: string; label: string }[] {
  const fiscal = cal.fiscalYearStartMonth !== 1 ? "FY" : "";
  const current = periodKeyFor(cal, kind, now);
  const out: { key: string; label: string }[] = [];
  if (kind === "year") {
    for (let y = Number(current), i = 0; i < count; i++, y--)
      out.push({ key: String(y), label: `${fiscal}${y}` });
    return out;
  }
  const [y0, q0] = current.split("-Q").map(Number) as [number, number];
  for (let i = 0, y = y0, q = q0; i < count; i++) {
    out.push({ key: `${y}-Q${q}`, label: `Q${q} ${fiscal}${y}` });
    q -= 1;
    if (q === 0) [y, q] = [y - 1, 4];
  }
  return out;
}
